import { useEffect, useState } from 'react';
import { LineItem } from '../buildBillForm.helper';
import { Box, RailzCategory, RailzTaxRate, RailzTrackingCategory, UtilButton } from 'vopay';
import InvoiceTableRow from './BillTableRow';
import styles from './BillTable.module.scss';

type ExtendedLineItem = LineItem & { isValid: boolean; hasBeenTouched: boolean };

interface BillTableProps {
  onChange: (taxItems: LineItem[], isValid: boolean) => void;
  afterHasTouched: boolean;
  salesTaxes: RailzTaxRate[];
  trackingCategories: RailzTrackingCategory[];
  categories: RailzCategory[];
  taxCalculation: 'inclusive' | 'exclusive' | 'outOfScope';
}

const BillTable: React.FC<BillTableProps> = ({
  onChange,
  salesTaxes,
  trackingCategories,
  afterHasTouched,
  taxCalculation,
  categories,
}) => {
  const emptyRecord = {
    Description: '',
    Quantity: '',
    Amount: '',
    TaxRateID: '',
    TrackingCategoryRefs: '',
    TaxAmount: '',
    AccountRefID: '',
    BillableStatus: false, // find out what it is upposed to be
    isValid: false,
    hasBeenTouched: afterHasTouched,
  };
  const [lineItems, setLineItems] = useState<ExtendedLineItem[]>([emptyRecord]);
  const addRow = () => {
    const updatedLineItems = [...lineItems, emptyRecord];
    setLineItems(updatedLineItems);
    onChange(
      updatedLineItems,
      updatedLineItems.every((item) => item.isValid),
    );
  };
  const removeRow = (index: number) => {
    const updatedLineItems = lineItems.filter((_, i) => i !== index);
    setLineItems(updatedLineItems);
    onChange(
      updatedLineItems,
      updatedLineItems.every((item) => item.isValid),
    );
  };
  const updateRow = (index: number, lineItem: LineItem, isValid: boolean) => {
    const updatedLineItems = lineItems.map((item, i) => {
      if (i === index) {
        return {
          ...lineItem,
          isValid,
          hasBeenTouched: true,
        };
      }
      return item;
    });
    setLineItems(updatedLineItems);
    onChange(
      updatedLineItems,
      updatedLineItems.every((item) => item.isValid),
    );
  };

  useEffect(() => {
    if (afterHasTouched) {
      const updatedLineItems = lineItems.map((item) => {
        return {
          ...item,
          hasBeenTouched: true,
        };
      });
      setLineItems(updatedLineItems);
      onChange(
        updatedLineItems,
        updatedLineItems.every((item) => item.isValid),
      );
    }
  }, [afterHasTouched]);

  return (
    <Box gapSize="12px" flx="col">
      <table className={styles.table}>
        <thead>
          <tr>
            <th>#</th>
            <th>Category</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Total</th>
            {taxCalculation !== 'outOfScope' && <th>Sales Tax</th>}
            <th>Billable</th>
            <th>Class</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, index) => {
            const nextRowUpdated = lineItems.slice(index + 1).some((nextItem) => nextItem.hasBeenTouched);
            return (
              <InvoiceTableRow
                key={index.toString()}
                index={index}
                salesTaxes={salesTaxes}
                trackingCategories={trackingCategories}
                values={item}
                nextRowUpdated={nextRowUpdated}
                taxCalculation={taxCalculation}
                categories={categories}
                onChange={updateRow}
                onDelete={removeRow}
              />
            );
          })}
        </tbody>
      </table>
      <UtilButton
        iconLeftProps={{
          icon: 'add',
        }}
        size="medium"
        onClick={addRow}
      >
        Add More
      </UtilButton>
    </Box>
  );
};

export default BillTable;
